import Configuration from '@/agilis/util/Configuration'
import DataTableTitle from '../TrackerDataTableTitle/TrackerDataTableTitle.vue'
import toursMixin from '@/agilis/components/Tour/mixins/toursMixin'
import FormSelectMultiple from '@/agilis/components/Form/FormSelectMultiple/FormSelectMultiple.vue'
import AreaTree from '@/applications/sales/components/AreaTree/AreaTree.vue'
import ErrorUtils from '@/agilis/util/Error'
import L from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import Vue from 'vue'

export default {
  name: 'TrackerDataTableItemEdit',
  mixins: [toursMixin],

  props: {
    api: String,
    propTitle: String,
    mode: String,
    configuration: Object,
    itemContext: {
      type: Object,
      default: () => {
        return {
          parentDTO: null,
          trackerId: null,
          applicationId: null,
          workspaceId: null,
          trackerTypeId: null
        }
      },
      required: false
    }
  },

  components: {
    DataTableTitle
  },

  data () {
    return {
      itemDTO: Configuration.datatable.getDTO(this.configuration),
      formCustomComponents: {
        FormSelectMultiple,
        AreaTree
      }
    }
  },

  mounted: function () {
    L.merge(this.itemDTO, {
      applicationId: this.applicationId,
      workspaceId: this.workspaceId,
      trackerTypeId: this.trackerTypeId,
      parentId: this.parentTrackerId != null ? this.parentTrackerId + '' : null
    })
    if (this.parentTrackerId != null) {
      Vue.set(this.itemDTO, 'parentId', this.parentTrackerId + '')
    }
    if (this.mode === 'edit') {
      this.fetchData()
    } else if (this.mode === 'insert') {
      const itemDTO = this.$router.agilis.getRouteData()
      itemDTO && L.merge(this.itemDTO, itemDTO)
      Configuration.datatable.mapItemDTOForEdit(this.configuration, this.itemDTO)
      this.checkColFromParent()
    }
  },

  computed: {
    ...mapGetters({
      selectedContext: 'contextStore/selectedContext',
      contextApplicationId: 'contextStore/applicationId',
      contextWorkspaceId: 'contextStore/workspaceId',
      contextTrackerTypeId: 'contextStore/trackerTypeId',
      trackerType: 'contextStore/trackerType',
      trackerTypes: 'contextStore/trackerTypes',
      contextParentTrackerId: 'contextStore/parentTrackerId',
      contextTrackerId: 'contextStore/trackerId',
      loading: 'loaderStore/loading'
    }),
    applicationId () {
      return this.itemContext.applicationId || this.contextApplicationId
    },
    workspaceId () {
      return this.itemContext.workspaceId || this.contextWorkspaceId
    },
    trackerTypeId () {
      return this.itemContext.trackerTypeId || this.contextTrackerTypeId
    },
    trackerId () {
      return this.itemContext.trackerId || this.contextTrackerId
    },
    parentTrackerId () {
      return this.itemContext.parentDTO?.id || this.contextParentTrackerId
    },
    parentDTO () {
      return this.itemContext.parentDTO || this.$route.params.parentDTO
    },
    formFields () {
      // Datatable columns
      const datatableColumns = L.cloneDeep(this.configuration.columns)
      for (const colConfig of Object.values(datatableColumns)) {
        if (colConfig.url) { // insert tracker context in api paths where necessary
          colConfig.url = this.$axios.agilisUrlAddContext(colConfig.url, this.selectedContext)
        }
        if (colConfig.api) {
          colConfig.api.url = this.$axios.agilisUrlAddContext(colConfig.api.url, this.selectedContext)
        }
      }

      // Form graphical elements
      const formElements = this.configuration.formElements
      return {
        ...datatableColumns,
        ...formElements
      }
    },

    trackerForRendering () {
      return Configuration.datatable.mapItemDTO(this.configuration, this.itemDTO)
    },

    computedTitle () {
      if (this.mode === 'insert') {
        return this.propTitle
      }

      if (this.configuration?.itemTitle) {
        return this.$t(this.configuration.itemTitle, this.trackerForRendering)
      }

      return this.propTitle + ' ' + this.trackerId
    }
  },

  methods: {
    ...mapActions({
      'notifyError': 'notificationStore/notifyError',
      'startLoader': 'loaderStore/startLoader',
      'stopLoader': 'loaderStore/stopLoader'
    }),
    setInheritedCol (sourceDTO, propagateValueMap) {
      if (!sourceDTO || !propagateValueMap?.length) return
      propagateValueMap.forEach(el => {
        this.itemDTO[el.to] = sourceDTO[el.from]
      })
    },
    checkColFromParent () {
      const parentTrackerTypeId = this.parentDTO?.trackerTypeId
      const parentConfig = parentTrackerTypeId ? Configuration.getTrackerType(this.applicationId, parentTrackerTypeId) : null
      if (!Array.isArray(parentConfig?.childrenTypes)) return
      const propagateValueMap = parentConfig.childrenTypes
        .filter(childType => {
          return typeof childType === 'object' && childType.trackerTypeId === this.trackerTypeId
        })
        .flatMap(childType => childType.propagateValueMap || [])
      this.setInheritedCol(this.parentDTO, propagateValueMap)
    },
    fetchData () {
      this.startLoader({ id: 'edit-tracker' })
      const url = this.$axios.agilisUrlDataTableEdit(this.api, this.trackerId)
      this.$axios
        .get(
          url,
          this.$axios.agilisRequestConfig(url))
        .then((response) => {
          L.merge(this.itemDTO, response.data)
          Configuration.datatable.mapItemDTOForEdit(this.configuration, this.itemDTO)
        })
        .catch((error) => {
          this.notifyError(error)
        })
        .finally(() => {
          this.stopLoader({ id: 'edit-tracker' })
        })
    },

    async saveItem (itemDTO) {
      let savedModel

      try {
        savedModel = Configuration.datatable.mapItemDTOForSave(this.configuration, JSON.parse(JSON.stringify(this.itemDTO)))
      } catch (error) {
        this.notifyError(error)
        return
      }

      this.startLoader({ id: 'save-tracker' })

      if (this.mode === 'edit') {
        const url = this.$axios.agilisUrlDataTableEdit(this.api, this.trackerId)

        try {
          await this.$axios.put(
            url,
            savedModel,
            this.$axios.agilisRequestConfig(url, null, { applicationId: this.applicationId, workspaceId: this.workspaceId })
          )

          // Perform custom activities at the end of the EDIT process
          if (this.afterEditHook && typeof this.afterEditHook === 'function') {
            await this.afterEditHook(savedModel)
          }
          this.$emit('edit-done', this.itemDTO)
          // Parent component may not have a parentDTO so we check using the trackerTypeId
          if (!this.itemContext.trackerTypeId) {
            this.$router.agilis.viewTrackerDTO(this.itemDTO)
          }
        } catch (error) {
          const errorMessage = ErrorUtils.getErrorMessage(error, savedModel.trackerTypeId, this.applicationId)
          this.notifyError(errorMessage)
        }
        this.stopLoader({ id: 'save-tracker' })
      } else {
        const url = this.$axios.agilisUrlDataTable(this.api)
        let agilisRequestConfig = this.$axios.agilisRequestConfig(url, null, { applicationId: this.applicationId, workspaceId: this.workspaceId })
        this.$axios
          .post(
            url,
            savedModel,
            agilisRequestConfig
          )
          .then((response) => {
            this.$emit('edit-done', response.data)
            // Parent component may not have a parentDTO so we check using the trackerTypeId
            if (!this.itemContext.trackerTypeId) {
              this.$router.agilis.viewTrackerDTO(response.data)
            }
          })
          .catch((error) => {
            const errorMessage = ErrorUtils.getErrorMessage(error, savedModel.trackerTypeId, this.applicationId)
            this.notifyError(errorMessage)
          })
          .finally(() => {
            this.stopLoader({ id: 'save-tracker' })
          })
      }
    },
    cancelItem (itemDTO, event) {
      this.$emit('cancel')
      if (!this.itemContext.parentDTO) {
        this.itemDTO.id ? this.$router.agilis.viewTracker({
          trackerId: this.itemDTO.id
        }) : this.$router.agilis.viewTrackerTable()
      }
    },

    handleSelectChange (item) {
      // copy selected item properties to DTO
      L.merge(this.itemDTO, item)
    },

    userCanAccessField (colId) {
      return Configuration.auth.userCanAccessField(this.selectedContext, colId)
    },

    userCanEditField (colId) {
      return Configuration.auth.userCanAccessField(this.selectedContext, colId, null, 'edit')
    },

    fieldTitle (colConfig) {
      return Configuration.datatable.getRequiredLabel(colConfig)
    }
  }
}
